import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, ClipboardCopy, BadgeCheck, Clock, PlayCircle } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoCampaignDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams();

  const token = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);

  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API}/promo/campaigns/${campaignId}?token=${encodeURIComponent(token)}`);
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          toast.error(err.detail || "Unable to load campaign");
          setLoading(false);
          return;
        }
        const data = await resp.json();
        setCampaign(data);
      } catch (e) {
        toast.error("Unable to load campaign");
      } finally {
        setLoading(false);
      }
    };

    if (!campaignId || !token) {
      setLoading(false);
      return;
    }

    load();
  }, [campaignId, token]);

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const statusIcon = (s) => {
    if (s === "active") return <PlayCircle className="text-[#D4AF37]" size={18} />;
    if (s === "complete") return <BadgeCheck className="text-green-400" size={18} />;
    return <Clock className="text-[#A1A1AA]" size={18} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] relative overflow-hidden">
        <button
          onClick={() => navigate("/promo")}
          className="absolute top-6 left-6 z-20 flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>

        <div className="min-h-screen flex items-center justify-center px-6">
          <Card className="bg-[#121214] border-[#27272A] w-full max-w-xl">
            <CardContent className="pt-6 text-center">
              <p className="text-[#FAFAFA] font-semibold">Campaign not found</p>
              <p className="text-sm text-[#A1A1AA] mt-2">Check your link token or return to the portal.</p>
              <Button className="btn-gold mt-4" onClick={() => navigate("/promo")}>Go to Promo Boost Portal</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const links = campaign.links || {};

  const smartLinks = [
    { label: "Primary", url: links.primary_link },
    { label: "Spotify", url: links.spotify },
    { label: "Apple Music", url: links.apple_music },
    { label: "YouTube (Video)", url: links.youtube_video },
    { label: "YouTube (Channel)", url: links.youtube_channel },
    { label: "SoundCloud", url: links.soundcloud },
    { label: "Audiomack", url: links.audiomack },
    { label: "Tidal", url: links.tidal },
    { label: "Amazon Music", url: links.amazon_music },
    { label: "Deezer", url: links.deezer },
    { label: "Bandcamp", url: links.bandcamp },
    { label: "Pandora", url: links.pandora },
  ].filter((x) => !!x.url);

  return (
    <div className="min-h-screen bg-[#0A0A0B] relative overflow-hidden" data-testid="promo-campaign-dashboard">
      <div className="absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-3xl"></div>
      </div>

      <button
        onClick={() => navigate("/promo")}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
      >
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>

      <section className="hero">
        <div className="hero-bg-reflection" aria-hidden>
          <div className="hero-bg-reflection-text">
            <img src="/logo-reflection-trimmed.png" alt="" className="hero-bg-reflection-logo" />
          </div>
        </div>
        <div className="hero-inner">
          <div className="hero-logo">
            <img src="/logo-reflection-trimmed.png" alt="DopeInHD" className="hero-logo-img" />
          </div>
          <p className="hero-subtitle">Campaign Dashboard</p>
        </div>
      </section>

      <div className="relative z-10 px-6 pb-16 w-full max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 bg-[#121214] border-[#27272A]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">{campaign.artist_name}</h1>
                  <p className="text-[#A1A1AA] mt-1">{campaign.title}</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                  {statusIcon(campaign.status)}
                  <span className="capitalize">{campaign.status}</span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border border-[#27272A] bg-[#1A1A1D]">
                  <p className="text-xs text-[#71717A]">Campaign ID</p>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <p className="text-sm text-[#FAFAFA] font-semibold truncate">{campaign.id}</p>
                    <Button variant="ghost" size="sm" className="text-[#D4AF37]" onClick={() => copy(campaign.id)}>
                      <ClipboardCopy size={16} />
                    </Button>
                  </div>
                </div>
                <div className="p-4 rounded-xl border border-[#27272A] bg-[#1A1A1D]">
                  <p className="text-xs text-[#71717A]">Package</p>
                  <p className="text-sm text-[#FAFAFA] font-semibold mt-1">
                    {campaign.package_name} — {campaign.duration_days} days
                  </p>
                  <p className="text-xs text-[#A1A1AA] mt-1">${campaign.amount} USD</p>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-[#FAFAFA]">Smart link hub</h3>
                <p className="text-xs text-[#71717A] mt-1">
                  These are your submitted links. We run promotion + traffic campaigns to increase plays/streams (no guarantees).
                </p>

                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {smartLinks.map((l) => (
                    <button
                      key={l.label}
                      onClick={() => window.open(l.url, "_blank")}
                      className="w-full text-left rounded-xl border border-[#27272A] bg-[#121214] hover:border-[#D4AF37]/60 transition-all px-4 py-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-[#FAFAFA]">{l.label}</p>
                          <p className="text-xs text-[#71717A] truncate max-w-[28ch]">{l.url}</p>
                        </div>
                        <ExternalLink size={16} className="text-[#D4AF37]" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {(campaign.notes || campaign.genre || campaign.mood) && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-[#FAFAFA]">Details</h3>
                  <div className="mt-2 text-sm text-[#A1A1AA] space-y-2">
                    {campaign.genre && <p><span className="text-[#71717A]">Genre:</span> {campaign.genre}</p>}
                    {campaign.mood && <p><span className="text-[#71717A]">Mood:</span> {campaign.mood}</p>}
                    {campaign.notes && <p><span className="text-[#71717A]">Goals:</span> {campaign.notes}</p>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#121214] border-[#27272A]">
            <CardContent className="pt-6">
              <h3 className="text-sm font-semibold text-[#FAFAFA]">Receipt</h3>
              <p className="text-xs text-[#71717A] mt-1">Order info from Stripe session.</p>

              <div className="mt-4 space-y-3">
                <div className="p-3 rounded-lg bg-[#1A1A1D] border border-[#27272A]">
                  <p className="text-xs text-[#71717A]">Stripe session</p>
                  <p className="text-xs text-[#A1A1AA] mt-1 break-all">{campaign.stripe_session_id || "—"}</p>
                </div>

                <div className="p-3 rounded-lg bg-[#1A1A1D] border border-[#27272A]">
                  <p className="text-xs text-[#71717A]">Purchaser</p>
                  <p className="text-xs text-[#A1A1AA] mt-1">{campaign.email}</p>
                  <p className="text-xs text-[#A1A1AA] mt-1">{campaign.phone}</p>
                </div>
              </div>

              <Button className="w-full btn-gold mt-5" onClick={() => navigate("/promo")}>Submit another</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PromoCampaignDashboard;
